import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Pattern, match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {useAiAssistantPresentation} from '@dms/ai-assistant';
import {useGetTenantV2Query} from '@dms/api/base';
import {
  useGetChecklistQuery,
  useListCustomerMessagesQuery,
  useListSignaturesQuery,
} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  useGetBusinessCaseActionsQuery,
  useGetBusinessCaseActivityListQuery,
  useGetBusinessCaseQuery,
  useGetBusinessCaseTestDriveQuery,
  useGetBusinessCaseUpcomingTasksQuery,
  useGetCheckoutForBusinessCaseQuery,
  useGetVehicleQuery,
  useListBusinessCaseTestDrivesQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {usePricePermissions, useUserFullName} from '@dms/shared';

import {
  DEFAULT_BUSINESS_CASE_SIGNAL_POLICY,
  type BusinessCaseSignalPolicy,
} from '@prompt-bridge/shared/ai-assistant-core';

import {toAssistantBusinessCaseSender} from '../utils/toAssistantBusinessCaseSender';
import {
  isSupportedBusinessCaseForAssistant,
  toBusinessCaseAssistantContext,
} from '../utils/toBusinessCaseAssistantContext';
import {usePublishAssistantBusinessCaseContext} from './usePublishAssistantBusinessCaseContext';

type UseAssistantBusinessCaseContextParams = {
  businessCaseId: string;
  signalPolicy?: BusinessCaseSignalPolicy;
};

const isOptionalQueryReady = (hasData: boolean, isError: boolean, isSuccess: boolean) =>
  match({hasData, isError, isSuccess})
    .with({hasData: true}, () => true)
    .with({isError: true}, () => true)
    .with({isSuccess: true}, () => true)
    .otherwise(() => false);

// eslint-disable-next-line complexity -- Pre-existing violation
export function useAssistantBusinessCaseContext(params: UseAssistantBusinessCaseContextParams) {
  const {isOpen: isAssistantOpen} = useAiAssistantPresentation();
  const isAssistantEnabled = isFeatureEnabled(featureFlags.OMNETIC_AI_ASSISTANT);
  const shouldFetchBusinessCase = isAssistantEnabled && isAssistantOpen;
  const {
    currentData: businessCase,
    isError: isBusinessCaseError,
    refetch: refetchBusinessCase,
  } = useGetBusinessCaseQuery(
    {businessCaseId: params.businessCaseId},
    {skip: !shouldFetchBusinessCase}
  );
  const shouldFetchDetails =
    shouldFetchBusinessCase &&
    isNotNil(businessCase) &&
    isSupportedBusinessCaseForAssistant(businessCase);
  const isSelling = shouldFetchDetails && businessCase.businessCaseType === 'SELLING';
  const saleVehicleId = isSelling
    ? businessCase.offers[0]?.saleVehicles?.[0]?.vehicleId
    : undefined;
  const senderDisplayName = useUserFullName();
  const isTestDrivesV2Enabled = isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2);
  const {
    currentData: activities,
    isError: isActivitiesError,
    refetch: refetchActivities,
  } = useGetBusinessCaseActivityListQuery(
    {businessCaseId: params.businessCaseId},
    {skip: !shouldFetchDetails}
  );
  const {currentData: upcomingTasks, isError: isUpcomingTasksError} =
    useGetBusinessCaseUpcomingTasksQuery(
      {businessCaseId: params.businessCaseId},
      {skip: !shouldFetchDetails}
    );
  const customerMessagesQuery = useListCustomerMessagesQuery(
    shouldFetchDetails
      ? {
          customerId: businessCase.customerId,
          recordId: params.businessCaseId,
          resourceId: EntityResourceIds.businessCase,
        }
      : skipToken
  );
  const customerQuery = useGetCustomerV2Query(
    shouldFetchDetails ? {customerId: businessCase.customerId} : skipToken
  );
  const tenantQuery = useGetTenantV2Query(undefined, {skip: !shouldFetchDetails});
  const signaturesQuery = useListSignaturesQuery(undefined, {skip: !shouldFetchDetails});
  const checklistQuery = useGetChecklistQuery(
    shouldFetchDetails ? {resourceId: 'BUSINESS_CASE', recordId: params.businessCaseId} : skipToken
  );
  const checkoutQuery = useGetCheckoutForBusinessCaseQuery(
    shouldFetchDetails ? {businessCaseId: params.businessCaseId} : skipToken
  );
  const actionsQuery = useGetBusinessCaseActionsQuery(
    shouldFetchDetails ? {businessCaseId: params.businessCaseId} : skipToken
  );
  const vehicleQuery = useGetVehicleQuery(
    shouldFetchDetails && isNotNil(saleVehicleId) ? {vehicleId: saleVehicleId} : skipToken
  );
  const saleVehicleQuery = useGetSaleVehicleQuery(
    shouldFetchDetails && isNotNil(saleVehicleId) ? {vehicleId: saleVehicleId} : skipToken
  );
  const fallbackTestDriveQuery = useGetBusinessCaseTestDriveQuery(
    {businessCaseId: params.businessCaseId},
    {skip: !isSelling || isTestDrivesV2Enabled}
  );
  const testDrivesQuery = useListBusinessCaseTestDrivesQuery(
    {businessCaseId: params.businessCaseId},
    {skip: !isSelling || !isTestDrivesV2Enabled}
  );
  const {canReadBusinessCasePurchaseExpectedProfit, canReadBusinessCaseSaleProfit} =
    usePricePermissions({
      businessCaseRecordId: shouldFetchDetails ? params.businessCaseId : null,
      vehicleRecordId: shouldFetchDetails ? saleVehicleId : null,
    });
  const canReadMargin = canReadBusinessCasePurchaseExpectedProfit || canReadBusinessCaseSaleProfit;
  const customerMessagesReady = isOptionalQueryReady(
    isNotNil(customerMessagesQuery.currentData),
    customerMessagesQuery.isError,
    customerMessagesQuery.isSuccess
  );
  const checklistReady = isOptionalQueryReady(
    isNotNil(checklistQuery.currentData),
    checklistQuery.isError,
    checklistQuery.isSuccess
  );
  const checkoutReady = isOptionalQueryReady(
    isNotNil(checkoutQuery.currentData),
    checkoutQuery.isError,
    checkoutQuery.isSuccess
  );
  const actionsReady = isOptionalQueryReady(
    isNotNil(actionsQuery.currentData),
    actionsQuery.isError,
    actionsQuery.isSuccess
  );
  const vehicleReady =
    isNil(saleVehicleId) ||
    isOptionalQueryReady(
      isNotNil(vehicleQuery.currentData),
      vehicleQuery.isError,
      vehicleQuery.isSuccess
    );
  const activeTestDriveQuery = isTestDrivesV2Enabled ? testDrivesQuery : fallbackTestDriveQuery;
  const testDrivesReady =
    !isSelling ||
    isOptionalQueryReady(
      isNotNil(activeTestDriveQuery.currentData),
      activeTestDriveQuery.isError,
      activeTestDriveQuery.isSuccess
    );
  const assistantTestDrives = match({
    fallbackTestDrive: fallbackTestDriveQuery.currentData,
    isSelling,
    isTestDrivesV2Enabled,
    testDrives: testDrivesQuery.currentData,
  })
    .with({isSelling: false}, () => [])
    .with({isSelling: true, isTestDrivesV2Enabled: true}, ({testDrives}) => testDrives ?? null)
    .with({isSelling: true, isTestDrivesV2Enabled: false}, ({fallbackTestDrive}) =>
      isNotNil(fallbackTestDrive) ? [fallbackTestDrive] : null
    )
    .exhaustive();
  const signaturesLoading =
    shouldFetchDetails &&
    isNil(signaturesQuery.currentData) &&
    !signaturesQuery.isError &&
    (signaturesQuery.isLoading || signaturesQuery.isFetching);
  const hasRequiredQueryError =
    shouldFetchBusinessCase &&
    (isBusinessCaseError ||
      (shouldFetchDetails &&
        (isActivitiesError ||
          isUpcomingTasksError ||
          customerQuery.isError ||
          tenantQuery.isError)));
  const isContextLoading =
    shouldFetchBusinessCase &&
    !hasRequiredQueryError &&
    (isNil(businessCase) ||
      (shouldFetchDetails &&
        (isNil(activities) ||
          isNil(upcomingTasks) ||
          isNil(customerQuery.currentData) ||
          isNil(tenantQuery.data) ||
          !customerMessagesReady ||
          !checklistReady ||
          !checkoutReady ||
          !actionsReady ||
          !vehicleReady ||
          !testDrivesReady ||
          signaturesLoading)));
  const refreshAssistantBusinessCaseContext = async () => {
    if (!shouldFetchBusinessCase) {
      return;
    }

    await Promise.all([
      refetchBusinessCase(),
      ...(shouldFetchDetails ? [refetchActivities(), customerMessagesQuery.refetch()] : []),
    ]);
  };
  const createAssistantBusinessCaseContext = (evaluatedAt: string) =>
    match({
      shouldFetchDetails,
      businessCase,
      activities,
      upcomingTasks,
      customer: customerQuery.currentData,
      tenant: tenantQuery.data,
      customerMessagesReady,
      checklistReady,
      checkoutReady,
      actionsReady,
      vehicleReady,
      testDrivesReady,
      signaturesLoading,
      hasRequiredQueryError,
    })
      .with(
        {
          shouldFetchDetails: true,
          businessCase: Pattern.when(
            (candidate): candidate is NonNullable<typeof businessCase> =>
              isNotNil(candidate) && isSupportedBusinessCaseForAssistant(candidate)
          ),
          activities: Pattern.nonNullable,
          upcomingTasks: Pattern.nonNullable,
          customer: Pattern.nonNullable,
          tenant: Pattern.nonNullable,
          customerMessagesReady: true,
          checklistReady: true,
          checkoutReady: true,
          actionsReady: true,
          vehicleReady: true,
          testDrivesReady: true,
          signaturesLoading: false,
          hasRequiredQueryError: false,
        },
        ({
          businessCase: availableBusinessCase,
          activities: availableActivities,
          upcomingTasks: availableTasks,
          customer,
          tenant,
        }) =>
          toBusinessCaseAssistantContext({
            businessCase: availableBusinessCase,
            activities: availableActivities,
            customerMessages: customerMessagesQuery.currentData?.messages ?? null,
            upcomingTasks: availableTasks,
            testDrives: assistantTestDrives,
            checklist: checklistQuery.currentData ?? null,
            checkout: checkoutQuery.currentData ?? null,
            actions: actionsQuery.currentData ?? null,
            vehicle: vehicleQuery.currentData ?? null,
            saleVehicle: saleVehicleQuery.currentData ?? null,
            canReadMargin,
            completeness: {
              vehicleLoaded:
                isNil(saleVehicleId) || (vehicleQuery.isSuccess && saleVehicleQuery.isSuccess),
              actionsLoaded: actionsQuery.isSuccess,
              checkoutLoaded: checkoutQuery.isSuccess,
              checklistLoaded: checklistQuery.isSuccess,
              testDrivesLoaded: !isSelling || activeTestDriveQuery.isSuccess,
              messagesLoaded: customerMessagesQuery.isSuccess,
            },
            customer,
            tenant,
            sender: toAssistantBusinessCaseSender(senderDisplayName, {
              signatureCount: signaturesQuery.currentData?.length,
              isLoading: signaturesLoading,
              isError: signaturesQuery.isError,
            }),
            userLanguage: i18n.resolvedLanguage ?? i18n.language,
            policy: params.signalPolicy ?? DEFAULT_BUSINESS_CASE_SIGNAL_POLICY,
            evaluatedAt,
          }) ?? undefined
      )
      .otherwise(() => undefined);

  return usePublishAssistantBusinessCaseContext(
    createAssistantBusinessCaseContext,
    [
      params.businessCaseId,
      params.signalPolicy,
      shouldFetchDetails,
      businessCase,
      activities,
      upcomingTasks,
      customerMessagesQuery.currentData,
      customerQuery.currentData,
      tenantQuery.data,
      signaturesQuery.currentData,
      senderDisplayName ?? undefined,
      checklistQuery.currentData,
      checkoutQuery.currentData,
      actionsQuery.currentData,
      vehicleQuery.currentData,
      saleVehicleQuery.currentData,
      saleVehicleQuery.isSuccess,
      fallbackTestDriveQuery.currentData,
      testDrivesQuery.currentData,
      canReadMargin,
      customerMessagesReady,
      checklistReady,
      checkoutReady,
      actionsReady,
      vehicleReady,
      testDrivesReady,
      signaturesLoading,
      hasRequiredQueryError,
    ],
    isContextLoading,
    refreshAssistantBusinessCaseContext
  );
}
